import React, { useEffect, useMemo, useState } from 'react';
import { FiFilter, FiRotateCcw, FiX } from 'react-icons/fi';
import PartnerDateInput from '../forms/PartnerDateInput';
import {
  buildPartnerTableFilterOptions,
  getEmptyPartnerTableFilters,
  mergePartnerTableFilterOptions,
  normalizePartnerTableMultiFilterValue,
} from '../../Utils/partnerTableSortFilterUtils';

const PartnerTableFiltersButton = ({ onClick, hasActiveFilters = false }) => (
  <button
    type="button"
    className={`partner-table-filters-btn ${hasActiveFilters ? 'has-active-filters' : ''}`}
    onClick={onClick}
  >
    <FiFilter aria-hidden="true" />
    <span>Filters</span>
  </button>
);

const PartnerTableResetFiltersButton = ({ onClick, disabled = false }) => (
  <button
    type="button"
    className="partner-table-reset-filters-btn"
    onClick={onClick}
    disabled={disabled}
    aria-label="Reset filters"
  >
    <FiRotateCcw aria-hidden="true" />
    <span>Reset filters</span>
  </button>
);

const PartnerTableFiltersModal = ({
  isOpen,
  title,
  columns,
  projects,
  appliedFilters,
  getColumnValue,
  externalFilterOptions = {},
  onClose,
  onApply,
}) => {
  const filterableColumns = useMemo(
    () => columns.filter((column) => column.filterable),
    [columns],
  );

  const filterOptions = useMemo(
    () => mergePartnerTableFilterOptions(
      buildPartnerTableFilterOptions(projects, filterableColumns, getColumnValue),
      externalFilterOptions,
      appliedFilters,
    ),
    [projects, filterableColumns, getColumnValue, externalFilterOptions, appliedFilters],
  );

  const [draftFilters, setDraftFilters] = useState(() => getEmptyPartnerTableFilters(filterableColumns));

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const empty = getEmptyPartnerTableFilters(filterableColumns);
    const merged = { ...empty, ...appliedFilters };
    filterableColumns.forEach((column) => {
      if (column.filterType === 'multiSelect') {
        merged[column.key] = normalizePartnerTableMultiFilterValue(merged[column.key]);
      }
    });
    setDraftFilters(merged);
  }, [isOpen, appliedFilters, filterableColumns]);

  if (!isOpen) {
    return null;
  }

  const handleFilterChange = (columnKey, value) => {
    setDraftFilters((current) => ({
      ...current,
      [columnKey]: value,
    }));
  };

  const handleMultiSelectToggle = (columnKey, option, checked) => {
    setDraftFilters((current) => {
      const selected = normalizePartnerTableMultiFilterValue(current[columnKey]);
      const nextSelected = checked
        ? [...selected, option]
        : selected.filter((value) => value !== option);

      return {
        ...current,
        [columnKey]: [...new Set(nextSelected)],
      };
    });
  };

  const handleMultiSelectClear = (columnKey) => {
    setDraftFilters((current) => ({
      ...current,
      [columnKey]: [],
    }));
  };

  const handleReset = () => {
    setDraftFilters(getEmptyPartnerTableFilters(filterableColumns));
  };

  const handleApply = () => {
    onApply(draftFilters);
    onClose();
  };

  return (
    <div className="partner-table-filters-overlay" onClick={onClose}>
      <div
        className="partner-table-filters-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="partner-table-filters-title"
      >
        <div className="partner-table-filters-modal__header">
          <h2 id="partner-table-filters-title" className="partner-table-filters-modal__title">
            {title}
          </h2>
          <button
            type="button"
            className="partner-table-filters-modal__close"
            onClick={onClose}
            aria-label="Close filters"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>

        <div className="partner-table-filters-modal__body">
          <div className="partner-table-filters-grid">
            {filterableColumns.map((column) => {
              const options = filterOptions[column.key] || [];
              const useSelect = column.filterType !== 'multiSelect'
                && (column.filterOptionsSource === 'api'
                  || (column.filterType !== 'date'
                    && options.length > 0
                    && options.length <= 100));

              if (column.filterType === 'multiSelect') {
                const selectedValues = normalizePartnerTableMultiFilterValue(
                  draftFilters[column.key],
                );
                const selectedSet = new Set(selectedValues);

                return (
                  <div key={column.key} className="partner-table-filters-field partner-table-filters-field--multi">
                    <div className="partner-table-filters-label-row">
                      <span className="partner-table-filters-label">{column.label}</span>
                      {selectedValues.length ? (
                        <button
                          type="button"
                          className="partner-table-filters-multi-clear"
                          onClick={() => handleMultiSelectClear(column.key)}
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                    <div className="partner-table-filters-multi" role="group" aria-label={column.label}>
                      {options.map((option) => {
                        const inputId = `partner-filter-${column.key}-${option}`.replace(/\s+/g, '-');
                        return (
                          <label key={`${column.key}-${option}`} className="partner-table-filters-multi-option" htmlFor={inputId}>
                            <input
                              id={inputId}
                              type="checkbox"
                              checked={selectedSet.has(option)}
                              onChange={(event) => handleMultiSelectToggle(
                                column.key,
                                option,
                                event.target.checked,
                              )}
                            />
                            <span>{option}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              return (
                <div key={column.key} className="partner-table-filters-field">
                  <span className="partner-table-filters-label">{column.label}</span>
                  {column.filterType === 'date' ? (
                    <PartnerDateInput
                      value={draftFilters[column.key] || ''}
                      onChange={(value) => handleFilterChange(column.key, value)}
                      className="partner-table-filters-date-input"
                    />
                  ) : useSelect ? (
                    <select
                      className="partner-table-filters-input"
                      value={draftFilters[column.key] || ''}
                      onChange={(event) => handleFilterChange(column.key, event.target.value)}
                    >
                      <option value="">All</option>
                      {options.map((option) => (
                        <option key={`${column.key}-${option}`} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="partner-table-filters-input"
                      value={draftFilters[column.key] || ''}
                      placeholder="All"
                      onChange={(event) => handleFilterChange(column.key, event.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="partner-table-filters-modal__footer">
          <div className="partner-table-filters-modal__footer-group">
            <button
              type="button"
              className="partner-table-filters-modal__button partner-table-filters-modal__button--apply"
              onClick={handleApply}
            >
              Apply
            </button>
            <button
              type="button"
              className="partner-table-filters-modal__button partner-table-filters-modal__button--reset"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
          <div className="partner-table-filters-modal__footer-group partner-table-filters-modal__footer-group--right">
            <button
              type="button"
              className="partner-table-filters-modal__button partner-table-filters-modal__button--cancel"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { PartnerTableFiltersButton, PartnerTableResetFiltersButton };
export default PartnerTableFiltersModal;
